export const titleCase = str => {
  var splitStr = str.toLowerCase().split(" ");
  for (var i = 0; i < splitStr.length; i++) {
    // You do not need to check if i is larger than splitStr length, as your for does that for you
    // Assign it back to the array
    splitStr[i] =
      splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  // Directly return the joined string
  return splitStr.join(" ");
};

/**
 * Converts text to title case (handles empty strings)
 * @param {string} text - Text to convert
 * @returns {string} Title case text
 */
export const toTitleCase = (text = "") => {
  if (!text) return "";
  return text
    .split(/\s+/)
    .map(word => {
      if (!word) return word;
      // Convert to lowercase first
      const lowerWord = word.toLowerCase();
      // Capitalize first letter
      return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
    })
    .join(" ");
};

/**
 * Formats bid amount with proper comma separation and INR sign
 * If amount is >= 1 crore, displays in crore format with INR sign (e.g., "₹ 25 CR." or "₹ 2.55 CR")
 * Otherwise displays with INR sign and "/-" (e.g., "₹ 1,00,000/-")
 * @param {number|string} amount - The bid amount to format
 * @returns {string} Formatted bid amount string
 */
export const formatBidAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "" || amount === "N/A") {
    return "N/A";
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);

  if (Number.isNaN(numAmount) || numAmount < 0) {
    return "N/A";
  }

  const ONE_CRORE = 10000000; // 1,00,00,000

  // If amount is >= 1 crore, format in crore format with INR sign
  if (numAmount >= ONE_CRORE) {
    const crores = numAmount / ONE_CRORE;
    // Check if it's a whole number
    if (crores % 1 === 0) {
      return `₹ ${crores} CR`;
    } else {
      // Format to 2 decimal places if needed
      return `₹ ${crores.toFixed(2)} CR`;
    }
  }

  // For amounts < 1 crore, format with INR sign and "/-"
  const formatted = numAmount.toLocaleString('en-IN', {
    maximumFractionDigits: 0
  });

  return `₹ ${formatted}/-`;
};

/**
 * Formats RATE DIFF. for bidder tables: rupee amounts use {@link formatBidAmount};
 * percentage strings (e.g. "6%") and placeholders are shown as-is.
 * @param {number|string|null|undefined} value - API rate_difference / rateDifference
 * @returns {string}
 */
export const formatRateDifference = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "-") return "-";
    if (trimmed.includes("%")) return trimmed;
    const numAmount = parseFloat(trimmed.replace(/,/g, ""));
    if (!Number.isNaN(numAmount) && Number.isFinite(numAmount)) {
      if (numAmount < 0) {
        const formatted = formatBidAmount(Math.abs(numAmount));
        return formatted === "N/A" ? "-" : `-${formatted}`;
      }
      const formatted = formatBidAmount(numAmount);
      return formatted === "N/A" ? "-" : formatted;
    }
    return trimmed;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "-";
    if (value < 0) {
      const formatted = formatBidAmount(Math.abs(value));
      return formatted === "N/A" ? "-" : `-${formatted}`;
    }
    const formatted = formatBidAmount(value);
    return formatted === "N/A" ? "-" : formatted;
  }
  return String(value);
};

/**
 * Formats value in lakhs with proper comma separation (for dashboard display)
 * Converts rupee values to lakhs and formats with commas
 * @param {number|string} value - The value in rupees to format
 * @returns {string} Formatted value string in lakhs with commas (e.g., "2.50" or "0")
 */
export const formatValueInLakhs = (value) => {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return "0";
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (Number.isNaN(numValue) || numValue < 0) {
    return "0";
  }

  const ONE_LAKH = 100000; // 1,00,000

  // Convert to lakhs
  const lakhs = numValue / ONE_LAKH;

  // Format with Indian number system with commas, allow 2 decimal places
  const formatted = lakhs.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  return formatted;
};

/**
 * Formats value in crores with proper comma separation (for dashboard display)
 * Converts rupee values to crores and formats with commas
 * @param {number|string} value - The value in rupees to format
 * @returns {string} Formatted value string in crores with commas (e.g., "2.98" or "0")
 */
export const formatValueInCrores = (value) => {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return "0";
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (Number.isNaN(numValue) || numValue < 0) {
    return "0";
  }

  const ONE_CRORE = 10000000; // 1,00,00,000

  // Convert to crores
  const crores = numValue / ONE_CRORE;

  // Format with Indian number system with commas, allow 2 decimal places
  const formatted = crores.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  return formatted;
};

/**
 * Formats value automatically choosing between lakhs and crores based on the amount
 * Returns an object with formatted value and unit
 * @param {number|string} value - The value in rupees to format
 * @returns {object} Object with { value: string, unit: string } - e.g., { value: "2.50", unit: "L" } or { value: "2.98", unit: "Cr" }
 */
export const formatValueAuto = (value) => {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return { value: "0", unit: "Cr" };
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (Number.isNaN(numValue) || numValue < 0) {
    return { value: "0", unit: "Cr" };
  }

  const ONE_LAKH = 100000; // 1,00,000
  const ONE_CRORE = 10000000; // 1,00,00,000

  // If >= 1 crore, use crores
  if (numValue >= ONE_CRORE) {
    const crores = numValue / ONE_CRORE;
    const formatted = crores.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return { value: formatted, unit: "Cr" };
  }
  
  // If >= 1 lakh, use lakhs
  if (numValue >= ONE_LAKH) {
    const lakhs = numValue / ONE_LAKH;
    const formatted = lakhs.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return { value: formatted, unit: "L" };
  }

  // For values < 1 lakh, show in rupees with commas
  const formatted = numValue.toLocaleString('en-IN', {
    maximumFractionDigits: 0
  });
  return { value: formatted, unit: "" };
};

/**
 * Formats monetary values for Tender 360 KPI cards only.
 * Crore values keep the CR suffix, lakh values use the L suffix,
 * and thousand values retain their full numeric amount without a unit.
 */
export const formatTender360KpiValue = (value) => {
  const parsedValue =
    typeof value === "string"
      ? Number(value.replace(/,/g, "").trim())
      : Number(value);
  const amount =
    Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
  const ONE_LAKH = 100000;
  const ONE_CRORE = 10000000;

  let scaledValue = amount;
  let unit = "";

  if (amount >= ONE_CRORE) {
    scaledValue = amount / ONE_CRORE;
    unit = "CR";
  } else if (amount >= ONE_LAKH) {
    scaledValue = amount / ONE_LAKH;
    unit = "L";
  }

  return {
    value: scaledValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    unit,
  };
};

/**
 * Formats count/number with proper comma separation for better readability
 * @param {number|string} count - The count/number to format
 * @returns {string} Formatted count string with commas (e.g., "1,234" or "0")
 */
export const formatCount = (count) => {
  if (count === null || count === undefined || count === "" || count === "N/A") {
    return "0";
  }

  const numCount = typeof count === 'string' ? parseFloat(count) : Number(count);

  if (Number.isNaN(numCount) || numCount < 0) {
    return "0";
  }

  // Format with Indian number system (lakhs, crores) with commas
  const formatted = numCount.toLocaleString('en-IN', {
    maximumFractionDigits: 0
  });

  return formatted;
};

/**
 * Coerce query/API geography fields to a deduped list of non-empty strings.
 * Handles comma-separated strings, arrays of strings, nested arrays, and select-like { value, label } objects.
 * @param {unknown} value
 * @returns {string[]}
 */
export const normalizeGeographyList = (value) => {
  if (value == null || value === "") return [];

  const out = [];

  const pushExpanded = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return;
    if (s.includes(",")) {
      s.split(",").forEach((part) => {
        const p = String(part ?? "").trim();
        if (p) out.push(p);
      });
    } else {
      out.push(s);
    }
  };

  const walk = (v) => {
    if (v == null || v === "") return;
    if (Array.isArray(v)) {
      v.forEach(walk);
      return;
    }
    if (typeof v === "object") {
      const label = v.value ?? v.label;
      if (label != null && label !== "") {
        pushExpanded(label);
      }
      return;
    }
    pushExpanded(v);
  };

  walk(value);
  return [...new Set(out)];
};

/**
 * Brief search: comma-separated input (e.g. "road,cement,steel") or list → deduped string[].
 * Same splitting rules as {@link normalizeGeographyList}.
 * @param {unknown} value
 * @returns {string[]}
 */
export const parseBriefInputToList = (value) => normalizeGeographyList(value);
